"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ShieldAlert } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const pests = [{
  title: "Fire Ants",
  context: "Thriving in the sandy soils near the Caloosahatchee River, fire ants build aggressive, sprawling colonies that threaten families and pets.",
  solution: "Our targeted baiting eliminates the queen and destroys the entire mound.",
  link: "/PestControlPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_fire_ant_colony.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_fire_ant_colony.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_fire_ant_colony.png-wvc-sizes",
    alt: "Macro shot of aggressive fire ants in Florida soil"
  }
}, {
  title: "Cockroaches",
  context: "Florida's intense humidity makes North Fort Myers a haven for Palmetto bugs and German cockroaches seeking shelter indoors.",
  solution: "We create an impenetrable exterior barrier to keep roaches out for good.",
  link: "/PestControlPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_german_cockroach_detail.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_german_cockroach_detail.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_german_cockroach_detail.png-wvc-sizes",
    alt: "Close up of a cockroach in a humid environment"
  }
}, {
  title: "Mosquitoes",
  context: "Riverfront properties and marshy areas face relentless mosquito pressure year-round, carrying risks of disease.",
  solution: "Our monthly fogging treatments let you reclaim and enjoy your yard again.",
  link: "/MosquitoControlPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_mosquito_on_leaf.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_mosquito_on_leaf.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_mosquito_on_leaf.png-wvc-sizes",
    alt: "Macro view of a mosquito on a leaf in Florida wetlands"
  }
}, {
  title: "Spiders",
  context: "Wooded areas and riverbanks bring unwanted arachnids, including venomous species, into local homes and lanais.",
  solution: "We sweep webs and apply residual treatments to keep spiders at bay.",
  link: "/PestControlPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_spider_on_web.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_spider_on_web.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_spider_on_web.png-wvc-sizes",
    alt: "Detailed macro shot of a spider in its web"
  }
}, {
  title: "Termites",
  context: "Subterranean termites silently destroy homes across Lee County, causing thousands in unseen structural damage.",
  solution: "Advanced trenching and baiting systems protect your biggest investment.",
  link: "/TermiteTreatmentPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_subterranean_termite_worker.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_subterranean_termite_worker.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_subterranean_termite_worker.png-wvc-sizes",
    alt: "Macro view of a subterranean termite worker"
  }
}];
export default function LocalPestGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="localpestgrid" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4" ref={ref}>
        <div className={`text-center max-w-3xl mx-auto mb-12 md:mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center gap-2 mb-4 px-4 py-1.5 bg-foreground text-background rounded-lg font-default text-sm uppercase tracking-wider">
            <ShieldAlert className="w-4 h-4" />
            <span>Local Threats, Local Solutions</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default text-foreground mb-6 leading-tight">
            Targeted Pest Control for North Fort Myers
          </h2>
          <p className="text-lg md:text-xl font-serif text-foreground/80 leading-relaxed">
            We understand the unique pest challenges of our local geography. From the sandy soils to the Caloosahatchee riverbanks, we have a proven solution for every invader.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 md:gap-8">
          {pests.map((pest, i) => {
          const colSpanClass = i < 3 ? "lg:col-span-4" : "lg:col-span-6";
          const delayClass = `delay-[${i * 100}ms]`;
          return <div key={pest.title} data-index={i} className={`${colSpanClass} transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <Card className="h-full border-4 border-foreground bg-card flex flex-col overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="border-b-4 border-foreground relative group">
                    <AspectRatio ratio={16 / 9}>
                      <img src={pest.image.src} data-wvc-srcset={pest.image.srcset} data-wvc-sizes={pest.image.sizes} alt={pest.image.alt} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    </AspectRatio>
                    <div className="absolute inset-0 bg-foreground/10 group-hover:bg-transparent transition-colors duration-300" />
                  </div>
                  
                  <CardHeader className="pb-4 pt-6">
                    <CardTitle className="font-default text-3xl text-foreground uppercase tracking-wide">
                      {pest.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="flex-grow flex flex-col gap-4 pb-6">
                    <p className="font-serif text-foreground/80 text-lg leading-relaxed">
                      {pest.context}
                    </p>
                    <div className="mt-auto pt-4 border-t-2 border-foreground/10">
                      <p className="font-serif font-bold text-foreground text-lg">
                        {pest.solution}
                      </p>
                    </div>
                  </CardContent>
                  
                  <CardFooter className="pt-0 pb-6">
                    <Button asChild size="lg" className="w-full rounded-lg font-default text-lg h-14 bg-foreground text-background hover:bg-foreground/90 hover:scale-[1.02] transition-all duration-200">
                      <Link to={pest.link}>Eliminate {pest.title}</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}